import React, { useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  ListSubheader,
  TextField,
  Typography,
} from '@mui/material';

import { useGetEmployeeUsers } from '~/hooks/apis/users/useGetEmployeeUsers';
import useDebounced from '~/hooks/useDebounced';

import type { ProjectAssignee } from '~/services/ContractingService';

const SEARCH_DELAY_MS = 300;
const MIN_SEARCH_LENGTH = 2;
const ASSIGNABLE_EMAIL_DOMAIN = '@luxuryescapes.com';

interface Props {
  assignees: Array<ProjectAssignee>;
  suggestedAssignees: Array<ProjectAssignee>;
  onChange: (assignees: Array<ProjectAssignee>) => void;
}

export default function TaskAssigneePicker({ assignees, suggestedAssignees, onChange }: Props) {
  const [inputValue, setInputValue] = useState('');
  const search = useDebounced(inputValue.trim(), SEARCH_DELAY_MS);
  const hasSearch = search.length >= MIN_SEARCH_LENGTH;
  const { data: users = [], isFetching } = useGetEmployeeUsers(search, hasSearch);
  const isInputEmpty = !inputValue.trim();
  const searchOptions: Array<ProjectAssignee> = users
    .filter((user) => user.email?.toLowerCase().endsWith(ASSIGNABLE_EMAIL_DOMAIN))
    .map((user) => ({ id: user.id_account, name: user.full_name, email: user.email }));
  const options = isInputEmpty ? suggestedAssignees : searchOptions;
  const isAssigned = (option: ProjectAssignee) => assignees.some((item) => item.id === option.id);

  const toggleAssignee = (option: ProjectAssignee) => {
    onChange(isAssigned(option) ? assignees.filter((item) => item.id !== option.id) : [...assignees, option]);
  };

  let emptyMessage: string | null = null;
  if (isInputEmpty && !suggestedAssignees.length) {
    emptyMessage = 'Type a name to search Luxury Escapes users.';
  } else if (!isInputEmpty && !hasSearch) {
    emptyMessage = `Type at least ${MIN_SEARCH_LENGTH} characters.`;
  } else if (!isInputEmpty && !isFetching && !searchOptions.length) {
    emptyMessage = 'No users found.';
  }

  return (
    <Box sx={{ width: 320 }}>
      <Box sx={{ p: 1 }}>
        <TextField
          autoFocus
          fullWidth
          placeholder="Search Luxury Escapes users"
          size="small"
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: isFetching && <CircularProgress size={16} />,
            },
          }}
        />
      </Box>
      <Divider />

      <List dense sx={{ maxHeight: 280, overflowY: 'auto', py: 0.5 }}>
        {isInputEmpty && options.length > 0 && (
          <ListSubheader disableSticky sx={{ lineHeight: 2.5, typography: 'overline' }}>
            Assigned in this project
          </ListSubheader>
        )}
        {emptyMessage && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
            {emptyMessage}
          </Typography>
        )}
        {options.map((option) => (
          <ListItemButton key={option.id} selected={isAssigned(option)} onClick={() => toggleAssignee(option)}>
            <ListItemAvatar sx={{ minWidth: 40 }}>
              <Avatar sx={{ fontSize: 14, height: 28, width: 28 }}>{option.name.charAt(0)}</Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={option.name}
              secondary={option.email}
              title={option.email}
              slotProps={{ primary: { noWrap: true }, secondary: { noWrap: true } }}
            />
            {isAssigned(option) && <CheckIcon color="primary" fontSize="small" />}
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
}
